import { formatPhoneNumber } from "../../../shared/helpers/formUtils";
import { getCurrencySymbol } from "../../../utils/currencySymbols";

const PrintableRepairOrder = ({
  orderData,
  dealerLogoSrc,
  currentYear,
  t,
  currencyCode = "USD",
}) => {
  const activeRepairOrderItems =
    orderData?.repairOrderItem?.filter((item) => item.status !== 3) || [];

  const subtotalAmount =
    activeRepairOrderItems.reduce(
      (sum, s) => sum + (parseFloat(s.price) || 0),
      0,
    ) || 0;
  const taxRate = Number(orderData?.dealerShipData?.tax) || 0;
  const taxAmount = subtotalAmount * (taxRate / 100);
  const totalWithTax = subtotalAmount + taxAmount;

  return (
    <div className="print-pdf-wrapper" id="pdf-print-area">
      <style>{`
        @page { size: 8.5in 11in; margin: 0.2in; }
        @media print {
          body * { visibility: hidden; }
          #pdf-print-area, #pdf-print-area * { visibility: visible; }
          #pdf-print-area { position: absolute; left: 0; top: 0; width: 100vw; background: white; display: block !important; }
          .app-ui-wrapper, .no-print { display: none !important; }
          .footer { position: fixed; bottom: 0; left: 0; width: 100vw; text-align: center; font-size: 0.9em; color: #888; }
          .print-pdf-wrapper { box-shadow: none !important; }
          /* Prevent TOTAL (tfoot) from repeating on each printed page */
          .services-table tfoot { display: table-row-group; }
          .services-table thead { display: table-header-group; }
        }
        @media screen {
          .print-pdf-wrapper { display: none; }
        }
        .print-pdf-wrapper {
          max-width: 100%;
          min-height: auto;
          margin: 0 auto;
          background: #fff;
          padding: 8px 8px 16px 8px;
          font-family: Arial, Helvetica, sans-serif;
          box-shadow: 0 0 8px #bbb;
          position: relative;
        }
        .pdf-header { display: grid; grid-template-columns: 1fr auto 1fr; align-items: flex-start; border-bottom: 2px solid #222; padding-bottom: 8px; margin-bottom: 16px; }
        .dealer-info { font-size: 1.1em; text-align: left; }
        .logo { width: 100px; height: 50px; display: flex; align-items: center; justify-content: center; margin: 0 auto; }
        .service-meta { text-align: right; font-size: 1em; }
        .section { margin-bottom: 16px; }
        .section-flex { display: flex; gap: 2%; }
        .section-box { flex: 1; background: #fafafa; border-radius: 6px; padding: 10px 16px 10px 16px; }
        .section-title { font-weight: bold; font-size: 1.1em; margin-bottom: 6px; }
        .info-table { width: 100%; border-collapse: collapse; }
        .info-table td { padding: 3px 6px 3px 0; vertical-align: top; font-size: 1em; }
        .info-table .label { font-weight: 500; min-width: 60px; text-align: left; }
        .info-table .value { text-align: left; word-break: break-word; min-width: 120px; }
        .info-table .address-value { white-space: pre-line; }
        .services-table { width: 100%; border-collapse: separate; border-spacing: 0 2px; margin-top: 8px; }
        .services-table th, .services-table td { border: none; padding: 6px 8px; font-size: 1em; }
        .services-table th { background: #f7f7f7; font-weight: bold; }
        .services-table th.srno, .services-table td.srno { width: 36px; text-align: center; padding-left: 0; padding-right: 0; }
        .services-table td.value, .services-table th.value { text-align: right; }
        .total-row td { font-size: 1.1em; }
        .footer { margin-top: 40px; text-align: center; font-size: 0.95em; color: #888; }
        /* New total summary style (outside table) */
        .total-summary { font-weight: bold; text-align: right; border-top: 2px solid #222; padding-top: 6px; margin-top: 4px; }
        @media print {
          /* Keep header repeating, avoid footer repeating (already handled) */
          .services-table { page-break-inside: auto; }
          .services-table tr { page-break-inside: avoid; page-break-after: auto; }
          /* Keep TOTAL attached to the last few rows */
          .total-summary { break-inside: avoid; page-break-inside: avoid; page-break-before: avoid; }
        }
      `}</style>
      {/* Header */}
      <div className="pdf-header">
        <div className="dealer-info">
          <div style={{ fontWeight: "bold" }}>
            {orderData?.dealerShipData?.name || ""}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            {orderData?.dealerShipData?.address1} ,
            {orderData?.dealerShipData?.cityOrLocality} ,
            {orderData?.dealerShipData?.postalCodeOrZip}
          </div>
        </div>
        <div className="logo-container">
          <img
            className="logo"
            src={orderData?.dealerShipData?.logoPath || dealerLogoSrc}
            alt="dealer logo"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
        <div className="service-meta">
          <div>
            Service ID: <b>{orderData?.repairOrder?.id || ""}</b>
          </div>
          <div>
            Date:{" "}
            <b>
              {orderData?.repairOrder?.createdDate
                ? new Date(
                    orderData.repairOrder.createdDate,
                  ).toLocaleDateString()
                : ""}
            </b>
          </div>
        </div>
      </div>
      {/* Customer & Vehicle Section */}
      <div className="section section-flex">
        {/* Customer */}
        <div className="section-box">
          <div className="section-title">{t("customer")}</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">{t("name")}</td>
                <td className="value">
                  : {orderData?.customerData?.fullName || ""}
                </td>
              </tr>
              <tr>
                <td className="label">{t("address")}</td>
                <td className="value address-value">
                  :{" "}
                  {[
                    orderData?.customerData?.address1,
                    orderData?.customerData?.cityOrLocality,
                    orderData?.customerData?.postalCodeOrZip,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("phone")}</td>
                <td className="value">
                  :{" "}
                  {formatPhoneNumber(orderData?.customerData?.phoneNumber) ||
                    ""}
                </td>
              </tr>
              <tr>
                <td className="label">{t("email")}</td>
                <td className="value">
                  : {orderData?.customerData?.email || ""}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Vehicle */}
        <div className="section-box">
          <div className="section-title">{t("vehicle")}</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">{t("vin")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.vin ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("year")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.year ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("make")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.makeName ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("model")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.modelName ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("body")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.body ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("engine")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.engine ||
                    t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("mileage")}</td>
                <td className="value">
                  : {orderData?.repairOrder?.odometer || t("not_available")}
                </td>
              </tr>
              <tr>
                <td className="label">{t("sub_model")}</td>
                <td className="value">
                  :{" "}
                  {orderData?.repairOrder?.customerVehicle?.subModelName ||
                    t("not_available")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Services Table */}
      <div className="section">
        <div className="section-title">{t("services")}</div>
        <table className="services-table">
          <thead>
            <tr>
              <th className="srno">{t("sr.no")}</th>
              <th>{t("name")}</th>
              <th className="value">{t("rate")}</th>
            </tr>
          </thead>
          <tbody>
            {activeRepairOrderItems.map((s, idx) => (
              <tr key={s.id || idx}>
                <td className="srno">{idx + 1}</td>
                <td>{s.serviceName || ""}</td>
                <td className="value">
                  {getCurrencySymbol(currencyCode)}
                  {(s.price || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Replace your current total-summary block with this */}

<div
  className="total-summary"
  style={{
    borderTop: "1px solid #000000",
    paddingTop: "6px",
    marginTop: "4px",
    width: "100%",
  }}
>
  {/* Tax Row */}
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "36px auto 1fr auto",
      alignItems: "center",
      marginBottom: "4px",
      columnGap: "6px",
    }}
  >
    <span></span>
    <span style={{ fontWeight: "600", textAlign: "left" }}>
      {t("tax")} ({taxRate}%)
    </span>
    <span></span>
    <span
      style={{
        textAlign: "right",
        minWidth: "90px",
        paddingRight: "8px",
      }}
    >
      {getCurrencySymbol(currencyCode)}
      {taxAmount.toFixed(2)}
    </span>
  </div>

  {/* Total Row */}
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "36px auto 1fr auto",
      alignItems: "center",
      borderTop: "2px solid #000000",
      paddingTop: "4px",
      fontSize: "1.05em",
      fontWeight: "bold",
      columnGap: "6px",
    }}
  >
    <span></span>
    <span style={{ textAlign: "left" }}>{t("total")}</span>
    <span></span>
    <span
      style={{
        textAlign: "right",
        minWidth: "90px",
        paddingRight: "8px",
      }}
    >
      {getCurrencySymbol(currencyCode)}
      {totalWithTax.toFixed(2)}
    </span>
  </div>
</div>
      </div>
      {/* Footer */}
      <div className="footer">
        {t("footer_copyright", { year: currentYear })}
      </div>
    </div>
  );
};

export default PrintableRepairOrder;